import React, { useState } from 'react';
import {
    Button,
    Divider,
    Flex,
    Modal,
    Select,
    Stack,
    Stepper,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { VirtualHierarchyStoresStep } from './VirtualHierarchyStoresStep';
import { VirtualHierarchyGroupingStep } from './VirtualHierarchyGroupingStep';
import {
    formatUserLabel,
    getVirtualHierarchyStepErrors,
    toVirtualDivisionFormValues,
    toVirtualDivisionPayload,
} from './helpers';
import {
    SapDivisionOption,
    VirtualDivisionFormValues,
    VirtualDivisionInputPayload,
    VirtualDivisionRow,
    VirtualHierarchyUser,
    VirtualOpsAreaRow,
} from './types';

type Props = {
    opened: boolean;
    onClose: () => void;
    editingDivision?: VirtualDivisionRow;
    users: VirtualHierarchyUser[];
    sapDivisions: SapDivisionOption[];
    virtualOpsAreas: VirtualOpsAreaRow[];
    occupiedStoreIds: Set<string>;
    saving: boolean;
    onSubmit: (input: VirtualDivisionInputPayload) => Promise<void>;
};

export function VirtualHierarchyBuilderModal({
    opened,
    onClose,
    editingDivision,
    users,
    sapDivisions,
    virtualOpsAreas,
    occupiedStoreIds,
    saving,
    onSubmit,
}: Props) {
    const [activeStep, setActiveStep] = useState(0);
    const [managerSearch, setManagerSearch] = useState('');
    const form = useForm<VirtualDivisionFormValues>({
        initialValues: toVirtualDivisionFormValues(editingDivision),
        validate: values => getVirtualHierarchyStepErrors(activeStep, values),
    });

    const userOptions = users.map(user => ({
        value: user.id,
        label: formatUserLabel(user),
    }));

    const handleClose = () => {
        setActiveStep(0);
        setManagerSearch('');
        onClose();
    };

    const handleManagerChange = (value: string | null) => {
        form.setFieldValue('managerUserId', value ?? '');
        const selected = userOptions.find(option => option.value === value);
        setManagerSearch(selected?.label ?? '');
    };

    const isStepValid = (step: number) => {
        const errors = getVirtualHierarchyStepErrors(step, form.values);
        return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
        const errors = getVirtualHierarchyStepErrors(activeStep, form.values);
        form.setErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }
        form.clearErrors();
        setActiveStep(current => Math.min(current + 1, 2));
    };

    const handleBack = () => {
        form.clearErrors();
        setActiveStep(current => Math.max(current - 1, 0));
    };

    const handleSubmit = async () => {
        const errors = getVirtualHierarchyStepErrors(2, form.values);
        form.setErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }
        try {
            await onSubmit(toVirtualDivisionPayload(form.values));
        } catch {
            return;
        }
        handleClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={
                editingDivision
                    ? 'Edit virtual division'
                    : 'Create virtual division'
            }
            centered
            size="lg"
            radius="md"
            classNames={{
                title: 'text-lg font-semibold',
            }}
        >
            <Stack gap="lg">
                <Stepper
                    active={activeStep}
                    size="sm"
                    color="primary"
                    allowNextStepsSelect={false}
                    classNames={{
                        steps: 'flex-nowrap',
                        step: 'data-[progress]:[&_.vh-step-label]:text-primary data-[progress]:[&_.vh-step-label]:font-medium data-[completed]:[&_.vh-step-label]:text-primary',
                        stepIcon:
                            'data-[progress]:!bg-[var(--step-color)] data-[progress]:!border-[var(--step-color)] data-[progress]:!text-white',
                        stepLabel: 'vh-step-label text-sm text-gray-400',
                    }}
                >
                    <Stepper.Step label="Division" />
                    <Stepper.Step label="Stores" />
                    <Stepper.Step label="Grouping" />
                </Stepper>

                <div className="h-80 overflow-y-auto px-12">
                    {activeStep === 0 ? (
                        <Stack gap="sm">
                            <TextInput
                                label="Virtual division name"
                                placeholder="Coast North"
                                {...form.getInputProps('name')}
                            />
                            <Select
                                label="Virtual divisional manager"
                                placeholder="Select a user"
                                searchable
                                clearable
                                data={userOptions}
                                value={form.values.managerUserId || null}
                                searchValue={managerSearch}
                                onSearchChange={setManagerSearch}
                                onChange={handleManagerChange}
                                error={form.errors.managerUserId}
                            />
                        </Stack>
                    ) : null}

                    {activeStep === 1 ? (
                        <VirtualHierarchyStoresStep
                            form={form}
                            sapDivisions={sapDivisions}
                            occupiedStoreIds={occupiedStoreIds}
                        />
                    ) : null}

                    {activeStep === 2 ? (
                        <VirtualHierarchyGroupingStep
                            form={form}
                            users={users}
                            sapDivisions={sapDivisions}
                            virtualOpsAreas={virtualOpsAreas}
                        />
                    ) : null}
                </div>

                <Divider />
                <Flex justify="flex-end" gap="sm">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        radius="xl"
                        fullWidth={false}
                        onClick={activeStep === 0 ? handleClose : handleBack}
                        disabled={saving}
                    >
                        {activeStep === 0 ? 'Cancel' : 'Back'}
                    </Button>
                    {activeStep < 2 ? (
                        <Button
                            type="button"
                            size="sm"
                            radius="xl"
                            fullWidth={false}
                            className="min-w-[9.5rem]"
                            onClick={handleNext}
                            disabled={!isStepValid(activeStep)}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            size="sm"
                            radius="xl"
                            fullWidth={false}
                            className="min-w-[9.5rem]"
                            loading={saving}
                            onClick={handleSubmit}
                        >
                            {editingDivision
                                ? 'Save changes'
                                : 'Create overlay'}
                        </Button>
                    )}
                </Flex>
            </Stack>
        </Modal>
    );
}
